import { AuditActionedBy } from 'types/api.graphql'
import { AssigneeLike, AuditLogLike } from 'types/vagueTypes'

type AdminUser = {
  user?: { firstName?: string; lastName?: string }
}

type Worker = {
  user?: { firstName?: string; lastName?: string }
}

export const getAdminUserName = (adminUser: AdminUser) => {
  if (!adminUser) return null
  return getUserName(adminUser.user)
}

export const getWorkerName = (worker: Worker) => {
  if (!worker) return null
  return getUserName(worker.user)
}

export const getUserName = (user: {
  firstName?: string
  lastName?: string
}) => {
  if (!user?.firstName && !user?.lastName) return ''

  if (user.firstName && !user.lastName) {
    return user.firstName.trim()
  }

  if (!user.firstName && user.lastName) {
    return user.lastName.trim()
  }

  return `${user.firstName.trim()} ${user.lastName.trim()}`
}

type AssigneeType = 'BuildPass user' | 'Worker' | 'Subcontractor'

export const getAssigneeName = (
  assignee: AssigneeLike
): [string, AssigneeType, string, string, string] => {
  let firstName: string
  let lastName: string
  let type: AssigneeType
  let userId: string
  let recordId: string

  if (assignee.adminUser) {
    firstName = assignee.adminUser.user.firstName
    lastName = assignee.adminUser.user.lastName
    userId = assignee.adminUser.user.id
    recordId = assignee.adminUser.id
    type = 'BuildPass user'
  } else if (assignee.worker) {
    firstName = assignee.worker.user.firstName
    lastName = assignee.worker.user.lastName
    userId = assignee.worker.user.id
    recordId = assignee.worker.id
    type = 'Worker'
  } else if (assignee.subcontractor) {
    firstName = assignee.subcontractor.company.name
    lastName = null
    userId = assignee.subcontractor.id
    recordId = assignee.subcontractor.id
    type = 'Subcontractor'
  }

  const name = `${firstName}${lastName ? ` ${lastName}` : ''}`.trim()

  return [name, type, assignee.id, userId, recordId]
}

export const getAuditLogName = (
  auditLog: AuditLogLike
): [string, string, string] => {
  let role
  let name
  let userId

  switch (auditLog.actionBy) {
    case AuditActionedBy.Admin:
      role = 'BuildPass user'
      break
    case AuditActionedBy.Guest:
      role = 'Public guest'
      break
    case AuditActionedBy.Integration:
      role = 'External integration'
      break
    case AuditActionedBy.Procore:
      role = 'PROCORE'
      break
    case AuditActionedBy.Subcontractor:
      role = 'Subcontractor'
      break
    case AuditActionedBy.SuperAdmin:
      role = 'BuildPass account manager'
      break
    case AuditActionedBy.Visitor:
      role = 'Public visitor'
      break
    case AuditActionedBy.Worker:
      role = 'Worker'
      break
    case AuditActionedBy.System:
      role = 'BuildPass system'
      break
    default:
      role = 'Unknown'
  }

  if (auditLog.actionByLink?.__typename) {
    if (auditLog.actionByLink?.__typename === 'SubcontractorEntity') {
      name = auditLog.actionByLink.name
      userId = auditLog.actionByLink.id
    }

    if (auditLog.actionByLink?.__typename === 'Subcontractor_ResourceLink') {
      name = auditLog.actionByLink.company.name
      userId = auditLog.actionByLink.id
    }

    if (
      auditLog.actionByLink?.__typename === 'AdminUserEntity' ||
      auditLog.actionByLink?.__typename === 'AdminUser_ResourceLink'
    ) {
      name = `${auditLog.actionByLink.user.firstName} ${auditLog.actionByLink.user.lastName}`
      userId = auditLog.actionByLink.user.id
    }

    if (
      auditLog.actionByLink?.__typename === 'WorkerAuditEntity' ||
      auditLog.actionByLink?.__typename === 'WorkerAudit_ResourceLink'
    ) {
      name = `${auditLog.actionByLink.user.firstName} ${auditLog.actionByLink.user.lastName}`
      userId = auditLog.actionByLink.user.id
    }
  }

  if (!name) {
    name = role
  }

  return [name, role, userId]
}
